/*
 * The admin site's words in Danish: the site section - line for line
 * site.en.ts, which is the contract.
 */

import type { siteEn } from './site.en.js';

/** "1 artikel", "2 artikler" - and the same for release notes. */
const articles = (count: number) => `${count} ${count === 1 ? 'artikel' : 'artikler'}`;
const releaseNotes = (count: number) => `${count} ${count === 1 ? 'udgivelsesnote' : 'udgivelsesnoter'}`;

/** A picker row's state tag, lower-case like the English it stands beside. */
const STATUS: Record<string, string> = {
  live: 'live',
  scheduled: 'planlagt',
  hidden: 'skjult',
  withdrawn: 'trukket tilbage',
  unwritten: 'uskrevet',
};

const CACHE = 'når den offentlige cache udløber, som standard efter et minut';

export const siteDa: typeof siteEn = {
  attachments: {
    title: 'Vedhæftede filer',
    intro:
      'Filer, som læserne kan hente. En artikel kan have op til ti på sin liste og kan linke til enhver af dem fra sin tekst; én fil kan bruges af mange artikler.',
    upload: 'Upload filer',
    uploadHint: 'Føj filer til biblioteket',
    noStore: 'Denne installation har intet lager til vedhæftede filer.',
    uploadPicker: 'Vælg filer til biblioteket',
    replacePicker: 'Vælg den nye fil',
    replacedNotice: (fileName) =>
      `Filen er erstattet: alle artikler, der har den på listen eller linker til den, tilbyder nu ${fileName}.`,
    addedNotice: (fileName) => `${fileName} er føjet til biblioteket.`,
    deletedNotice: (fileName) => `${fileName} er slettet fra biblioteket.`,
    find: 'Find en fil',
    findPlaceholder: 'Filnavn…',
    loading: 'Indlæser biblioteket…',
    empty: 'Biblioteket er tomt. Upload en fil her eller fra en artikels editor.',
    noMatch: 'Ingen fil matcher.',
    uploaded: 'uploadet {date} af {name}',
    replaced: 'erstattet {date} af {name}',
    notListed: 'ikke på nogen liste',
    notListedHint: 'Ingen artikel har den på sin liste. Der kan stadig linkes til den fra en tekst.',
    listedBy: (count) => `på listen i ${articles(count)}`,
    replace: 'Erstat',
    replaceHint: 'Upload en ny udgave af filen til alle artikler, der bruger den',
    replaceFile: (fileName) => `Erstat ${fileName}`,
    delete: 'Slet',
    deleteFile: (fileName) => `Slet ${fileName}`,
  },

  deleteAttachment: {
    title: (fileName) => `Slet ${fileName}?`,
    cancel: 'Annuller',
    confirm: 'Slet filen',
    finding: 'Finder de artikler, der bruger den…',
    unused: 'Ingen artikel har den på sin liste eller linker til den. Intet andet ændres.',
    usedBy: (count) =>
      `${count === 1 ? 'Én artikel' : `${count} artikler`} bruger denne fil. Sletter du den, forsvinder den fra alle lister, og et link til den i en tekst bliver til almindelig tekst:`,
    listed: 'på listen',
    linked: 'linket i teksten',
    permanent: 'Filen fjernes også fra lageret. Det kan ikke fortrydes.',
  },

  promoted: {
    loading: 'Indlæser fremhævede artikler…',
    title: 'Fremhævede artikler',
    intro: (max) =>
      `Op til ${max} artikler, der vises som felter over den offentlige artikelliste, i denne rækkefølge. Kun artikler, der er live, vises faktisk; resten venter her, til de bliver det.`,
    saving: 'Gemmer…',
    saveOrder: 'Gem rækkefølge',
    saved: 'De fremhævede artikler er gemt.',
    empty: 'Intet er fremhævet. Tilføj en artikel nedenfor.',
    notShown: 'vises ikke — artiklen er ikke live',
    alreadyFirst: (title) => `${title} er allerede først.`,
    alreadyLast: (title) => `${title} er allerede sidst.`,
    moved: (title, position, total) => `${title} er flyttet til plads ${position} af ${total}.`,
    moveUp: (title) => `Flyt ${title} op`,
    moveDown: (title) => `Flyt ${title} ned`,
    removeFrom: (title) => `Fjern ${title} fra de fremhævede artikler`,
    remove: 'Fjern',
    addTitle: 'Tilføj en artikel',
    full: (max) => `Listen er fuld (${max} af ${max}). Fjern en for at tilføje en anden.`,
    searchLabel: 'Søg efter artikler at fremhæve',
    searchPlaceholder: 'Søg i artikler…',
    searching: 'Søger…',
    noCandidates: 'Intet matcher, som ikke allerede er fremhævet.',
    status: (status) => STATUS[status] ?? status,
    add: 'Tilføj',
  },

  questions: {
    loading: 'Indlæser spørgsmålene…',
    title: 'Spørgsmål',
    intro: (max) =>
      `Op til ${max} spørgsmål, der vises på forsiden under »Hyppige Spørgsmål«, i denne rækkefølge. Hvert spørgsmål linker til den artikel, der besvarer det, og læserne ser kun de spørgsmål, hvis artikel de må læse.`,
    saving: 'Gemmer…',
    save: 'Gem spørgsmål',
    saved: 'Spørgsmålene er gemt.',
    empty: 'Der er ingen spørgsmål endnu. Tilføj et nedenfor.',
    questionLabel: (position) => `Spørgsmål ${position}`,
    linksTo: 'Linker til',
    notShown: 'vises ikke — artiklen er ikke live',
    privateArticle: 'privat — vises kun for dens læsere',
    blank: 'Skriv spørgsmålet, eller fjern det.',
    alreadyFirst: (question) => `»${question}« er allerede først.`,
    alreadyLast: (question) => `»${question}« er allerede sidst.`,
    moved: (question, position, total) => `»${question}« er flyttet til plads ${position} af ${total}.`,
    moveUp: (position) => `Flyt spørgsmål ${position} op`,
    moveDown: (position) => `Flyt spørgsmål ${position} ned`,
    removeFrom: (position) => `Fjern spørgsmål ${position}`,
    remove: 'Fjern',
    addTitle: 'Tilføj et spørgsmål',
    newQuestionLabel: 'Spørgsmål',
    newQuestionPlaceholder: 'Hvordan …?',
    newQuestionHint: 'Skriv først spørgsmålet, og vælg så den artikel, der besvarer det.',
    searchLabel: 'Find den artikel, der besvarer det',
    searchPlaceholder: 'Søg i artikler…',
    searching: 'Søger…',
    noCandidates: 'Ingen artikler matcher.',
    full: (max) => `Listen er fuld (${max} af ${max}). Fjern et spørgsmål for at tilføje et andet.`,
    status: (status) => STATUS[status] ?? status,
    add: 'Tilføj',
    addWith: (title) => `Tilføj spørgsmålet med et link til ${title}`,
    changeArticle: 'Skift artikel',
    changeArticleFor: (position) => `Skift den artikel, spørgsmål ${position} linker til`,
    changeSearchLabel: (position) => `Find den artikel, der besvarer spørgsmål ${position}`,
    choose: 'Vælg',
    chooseFor: (position, title) => `Lad spørgsmål ${position} linke til ${title}`,
    cancel: 'Annuller',
    cancelHint: 'Luk søgningen, og behold artiklen (Esc)',
    changed: (position, title) => `Spørgsmål ${position} linker nu til ${title}.`,
  },

  metadata: {
    loading: 'Indlæser metadata…',
    title: 'Metadata',
    intro:
      'Produkter og kategorier. Artikler henviser til dem, og udgivelsesnoter til deres produkt, så ændringer her slår igennem overalt på én gang.',
    deleted: (label, steps) => `»${label}« er slettet. ${steps}`,
    name: 'Navn',
    slug: 'Slug',
    articles: 'Artikler',
    releaseNotes: 'Udgivelsesnoter',
    state: 'Status',
    actions: 'Handlinger',
    active: 'Aktiv',
    retired: 'Udfaset',
    edit: 'Rediger',
    delete: 'Slet',
    usedByArticles: (count) => `Bruges af ${articles(count)}. Udfasning er som regel bedre.`,
    usedByReleaseNotes: (articleCount, releaseNoteCount) =>
      `Bruges af ${
        articleCount > 0
          ? `${articles(articleCount)} og ${releaseNotes(releaseNoteCount)}`
          : releaseNotes(releaseNoteCount)
      }. Et produkt kan ikke slettes, mens en udgivelsesnote bruger det; udfas det i stedet.`,
    cancel: 'Annuller',
    saving: 'Gemmer…',
    save: 'Gem',
    nameRequired: 'Der skal være et navn.',
    categoryNameHint: 'Vises kommasepareret på en artikel, så et navn kan ikke indeholde komma.',
    productColumn: 'Produkt',
    categoryProductHint:
      'Valgfrit - et eller flere, eller det tomme felt for ingen. Bruges ikke endnu: det skal give læserne mulighed for at vælge et produkt og se dets kategorier.',
    noProduct: 'Intet produkt',
    retiredProduct: (name) => `${name} (udfaset)`,
    product: {
      tab: 'Produkter',
      intro: 'Hvilke produkter en artikel eller en udgivelsesnote handler om. Læserne filtrerer efter dem.',
      newTag: 'Nyt produkt',
      editTag: (name) => `Rediger produkt: ${name}`,
      none: 'Der er endnu ingen produkter. En artikel skal have mindst ét.',
      slugHint:
        'Første led i adressen på hver artikel under dette produkt, andet led i adressen på hver udgivelsesnote og produktets filterlinks. Ændrer du det, flytter alle artikler og udgivelsesnoter, som produktet har.',
      retireHint:
        'Udfasning bevarer alle eksisterende tildelinger, men produktet tilbydes ikke længere til nye. Det er det, du vil, i stedet for at slette et produkt, der er i brug.',
    },
    category: {
      tab: 'Kategorier',
      intro: 'Hvilken slags artikel det er — vejledning, opslag, kendt problem.',
      newTag: 'Ny kategori',
      editTag: (name) => `Rediger kategori: ${name}`,
      none: 'Der er endnu ingen kategorier. En artikel skal have mindst én.',
      slugHint: 'Bruges i filteradresser. Ændrer du den, ændres alle gemte links, der filtrerer på kategorien.',
      retireHint:
        'Udfasning bevarer alle eksisterende tildelinger, men kategorien tilbydes ikke længere til nye. Det er det, du vil, i stedet for at slette en kategori, der er i brug.',
    },
  },

  settings: {
    loading: 'Indlæser indstillinger…',
    title: 'Indstillinger',
    intro:
      'Sådan præsenterer vidensbasen sig. En ændring vises her med det samme og når ud til læserne, når den offentlige cache udløber.',
    saving: 'Gemmer…',
    save: 'Gem',
    unsaved: 'Ikke-gemte ændringer',
    titleRequired: 'Der skal være en titel.',
    tooLong: (max) => `Højst ${max} tegn.`,
    knowledgeBase: 'Vidensbase',
    lastChanged: 'Senest ændret {date} · {name}',
    icon: 'Ikon',
    noIcon: 'Intet ikon uploadet - det indbyggede mærke vises.',
    removeIcon: 'Fjern ikon',
    iconHint: (maxSize) =>
      `Vises øverst til venstre på alle sider, i 28 pixel, i stedet for mærket »KB«. PNG, JPEG, GIF eller WEBP, højst ${maxSize}. Et kvadratisk billede ser bedst ud.`,
    iconTooLarge: (size, limit) => `Filen fylder ${size}. ${limit}`,
    iconUnreadable: 'Filen kunne ikke læses. Vælg den igen.',
    iconNotAnImage: 'Filen er ikke et billede, vi kan gemme.',
    iconUpdated: `Ikonet er opdateret. Du ser det nu; læserne får det, ${CACHE}.`,
    iconRemoved: 'Ikonet er fjernet. Det indbyggede mærke er tilbage.',
    siteTitle: 'Webstedets titel',
    siteTitleHint: 'Navnet i sidehovedet på alle sider og i browserfanen.',
    siteTitleSaved: `Gemt. Du ser det nye navn nu; læserne får det, ${CACHE}.`,
    headingTitle: 'Titel',
    headingSubtitle: 'Undertitel',
    headingTitleDanish: 'Dansk titel',
    headingSubtitleDanish: 'Dansk undertitel',
    danishHint: 'Valgfri. Står et felt tomt, ser danske læsere det engelske.',
    usesEnglish: (english) => `Tom: bruger »${english}«`,
    usesEnglishNone: 'Tom: ingen linje, som på engelsk',
    readersSee: 'Læserne ser',
    articles: {
      titleHint: 'Overskriften på artikelsiden, dens browserfane og linket tilbage til den fra hver artikel.',
      subtitleHint: 'Én linje under titlen på artikelsiden. Lad feltet være tomt, hvis der ikke skal være nogen.',
      saved: `Gemt. Læserne ser den nye titel på artikelsiden, ${CACHE}.`,
    },
    releaseNotes: {
      titleHint:
        'Overskriften på siden med udgivelsesnoter, dens browserfane og linket tilbage til den fra hver udgivelsesnote.',
      subtitleHint:
        'Én linje under titlen på siden med udgivelsesnoter. Lad feltet være tomt, hvis der ikke skal være nogen.',
      saved: `Gemt. Læserne ser den nye titel på siden med udgivelsesnoter, ${CACHE}.`,
    },
    theme: 'Tema',
    currentTheme: (theme) => `I brug: ${theme}`,
    thumbnailsHint: (realArticle) =>
      `Hvert miniaturebillede viser ${
        realArticle ? 'en tilfældigt valgt udgivet artikel' : 'en eksempelartikel'
      } i det tema. Klik på et for at prøve det på denne side; intet ændres for læserne, før du anvender det.`,
    applying: 'Anvender…',
    applyTheme: 'Anvend tema',
    themeApplied: (theme) => `Temaet ${theme} er anvendt. Du ser det nu; læserne får det, ${CACHE}.`,
    previewing: (previewed, saved) =>
      `Forhåndsviser ${previewed} kun på denne side. Læserne ser stadig ${saved}.`,
  },

  themePreview: {
    preview: (theme) => `Forhåndsvis temaet ${theme}`,
    primaryAction: 'Primær handling',
    secondary: 'Sekundær',
    current: 'I brug',
    sampleTitle: 'Sådan tilslutter du en datakilde',
    sampleCategory: 'Vejledning',
    sampleContent: [
      'En datakilde er det sted, en rapport henter sine tal fra. Denne vejledning tilslutter en',
      'og kontrollerer, at forbindelsen holder.',
      '',
      '## Før du begynder',
      '',
      '- En bruger med **læseadgang** til kildesystemet.',
      '- Serverens adresse og databasens navn.',
      '',
      '> Forbindelser testes, når du gemmer dem, så en forkert adgangskode fejler her',
      '> i stedet for i en rapport ved månedsafslutningen.',
      '',
      'Åbn **Indstillinger**, og vælg *Ny datakilde*. Se [vejledningen om rettigheder](#) for,',
      'hvad kontoen skal have, og brug knappen `Test`, før du gemmer.',
    ].join('\n'),
  },
};
